import * as $list from "../gleam_stdlib/gleam/list.mjs";
import * as $option from "../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../gleam_stdlib/gleam/option.mjs";
import * as $order from "../gleam_stdlib/gleam/order.mjs";
import { Gt } from "../gleam_stdlib/gleam/order.mjs";
import {
  toList,
  Empty as $Empty,
  prepend as listPrepend,
  CustomType as $CustomType,
  makeError,
  isEqual,
} from "./gleam.mjs";

const FILEPATH = "src/priorityq.gleam";

class PriorityQueue extends $CustomType {
  constructor(data, cmp) {
    super();
    this.data = data;
    this.cmp = cmp;
  }
}

class Empty extends $CustomType {}

class NonEmpty extends $CustomType {
  constructor($0) {
    super();
    this[0] = $0;
  }
}

class PairingTree extends $CustomType {
  constructor(val, children, size) {
    super();
    this.val = val;
    this.children = children;
    this.size = size;
  }
}

/**
 * Creates an empty priority queue.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * new(int.compare) // -> PriorityQueue(Int)
 * ```
 */
export function new$(cmp) {
  return new PriorityQueue(new Empty(), cmp);
}

function from_pairing_tree(tree, cmp) {
  return new PriorityQueue(new NonEmpty(tree), cmp);
}

function one(val, cmp) {
  return new PriorityQueue(
    new NonEmpty(new PairingTree(val, toList([]), 1)),
    cmp,
  );
}

/**
 * Returns whether the priority queue is empty.
 *
 * Runs in constant time.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * new(int.compare) |> is_empty() // -> True
 * from_list([0], int.compare) |> is_empty() // -> False
 * ```
 */
export function is_empty(pq) {
  return pq.data instanceof Empty;
}

/**
 * Returns the number of elements in the priority queue.
 *
 * Runs in constant time.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * from_list([1, 2, 3], int.compare) |> size() // -> 3
 * ```
 */
export function size(pq) {
  let $ = pq.data;
  if ($ instanceof Empty) {
    return 0;
  } else {
    let tree = $[0];
    return tree.size;
  }
}

/**
 * Returns the maximum value in the priority queue.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * new(int.compare) |> peek() // -> None
 * from_list([1, 10, 5], int.compare) |> peek() // -> 10
 * ```
 */
export function peek(pq) {
  let $ = pq.data;
  if ($ instanceof Empty) {
    return new None();
  } else {
    let tree = $[0];
    return new Some(tree.val);
  }
}

function merge(pq1, pq2) {
  let $ = isEqual(pq1.cmp, pq2.cmp);
  if ($) {
    let $1 = pq1.data;
    let $2 = pq2.data;
    if ($1 instanceof Empty) {
      return pq2;
    } else if ($2 instanceof Empty) {
      return pq1;
    } else {
      let tree1 = $1[0];
      let tree2 = $2[0];
      let new_size = tree1.size + tree2.size;
      let $3 = pq1.cmp(tree1.val, tree2.val);
      if ($3 instanceof Gt) {
        return new PriorityQueue(
          new NonEmpty(
            new PairingTree(
              tree1.val,
              listPrepend(tree2, tree1.children),
              new_size,
            ),
          ),
          pq1.cmp,
        );
      } else {
        return new PriorityQueue(
          new NonEmpty(
            new PairingTree(
              tree2.val,
              listPrepend(tree1, tree2.children),
              new_size,
            ),
          ),
          pq1.cmp,
        );
      }
    }
  } else {
    throw makeError(
      "panic",
      FILEPATH,
      "priorityq",
      118,
      "merge",
      "inconsistent cmp function",
      {}
    )
  }
}

/**
 * Pushes a value into the priority queue.
 *
 * Runs in constant time.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * new(int.compare) |> push(10) // -> PriorityQueue(Int)
 * ```
 */
export function push(pq, val) {
  return merge(one(val, pq.cmp), pq);
}

/**
 * Creates a priority queue from a list.
 *
 * Runs in linear time.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * from_list([1, 10, 5], int.compare) // -> PriorityQueue(Int)
 * ```
 */
export function from_list(ls, cmp) {
  let _pipe = new$(cmp);
  return ((_capture) => { return $list.fold(ls, _capture, push); })(_pipe);
}

function merge_pairs(trees, cmp) {
  if (trees instanceof $Empty) {
    return new PriorityQueue(new Empty(), cmp);
  } else {
    let $ = trees.tail;
    if ($ instanceof $Empty) {
      let tree = trees.head;
      return from_pairing_tree(tree, cmp);
    } else {
      let tree1 = trees.head;
      let tree2 = $.head;
      let rest = $.tail;
      let _pipe = merge(
        from_pairing_tree(tree1, cmp),
        from_pairing_tree(tree2, cmp),
      );
      return merge(_pipe, merge_pairs(rest, cmp));
    }
  }
}

/**
 * Pops the maximum value from the priority queue.
 *
 * Runs in amortized logarithmic time.
 *
 * ## Examples
 *
 * ```gleam
 * import gleam/int
 *
 * from_list([0]) |> pop() // -> PriorityQueue(Int)
 * ```
 */
export function pop(pq) {
  let $ = pq.data;
  if ($ instanceof Empty) {
    return pq;
  } else {
    let tree = $[0];
    return merge_pairs(tree.children, pq.cmp);
  }
}
